<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-blue-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-blue-600 tracking-widest">Administración</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Pago de Servicios Públicos
          <span class="px-2 sm:px-3 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-xs sm:text-sm font-black rounded-lg border border-blue-200 dark:border-blue-800">
            {{ pagos.length }}
          </span>
        </h1>
      </div>
      <div class="flex items-center gap-2 w-full sm:w-auto flex-wrap justify-end">
        <button @click="cargarTodo" :disabled="loading" class="px-3 sm:px-4 py-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-sm flex items-center gap-1 sm:gap-2">
          <Icon icon="mdi:refresh" :class="{ 'animate-spin': loading }" class="text-base" />
          <span class="hidden sm:inline">Actualizar</span>
        </button>
        <button @click="openPagoModal()" class="px-3 sm:px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center gap-1 sm:gap-2">
          <Icon icon="mdi:file-plus" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Nueva Factura</span>
          <span class="sm:hidden">Factura</span>
        </button>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-5">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Pendientes</p>
        <p class="text-xl sm:text-2xl font-black text-amber-600">{{ stats.pendientes }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Vencidos</p>
        <p class="text-xl sm:text-2xl font-black text-red-600">{{ stats.vencidos }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Pagados</p>
        <p class="text-xl sm:text-2xl font-black text-emerald-600">{{ stats.pagados }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Monto Pendiente</p>
        <p class="text-lg sm:text-xl font-black text-indigo-600">{{ formatearMoneda(stats.montoPendiente) }}</p>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- 🆕 CALENDARIO DE PRÓXIMOS VENCIMIENTOS -->
    <!-- ============================================ -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5">
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <div class="flex items-center gap-3">
          <div class="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
            <Icon icon="mdi:calendar-clock" class="text-white text-lg sm:text-xl" />
          </div>
          <div>
            <h3 class="text-sm sm:text-base font-black text-gray-900 dark:text-white">
              Próximos Vencimientos
            </h3>
            <p class="text-[10px] sm:text-xs text-gray-500">
              {{ totalPendientes }} pendiente(s) — {{ formatearMoneda(totalProximos7Dias) }} esta semana
            </p>
          </div>
        </div>
      </div>

      <!-- Grid de bloques -->
      <div class="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3">

        <!-- VENCIDOS -->
        <button @click="abrirCalendarioDetalle('vencidos')"
                class="text-left rounded-2xl p-3 sm:p-4 border-2 transition-all hover:shadow-md"
                :class="calendarioProximos.vencidos.length > 0
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800 hover:border-red-400'
                  : 'bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700'">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-1.5">
              <Icon icon="mdi:alert-circle" class="text-red-600 dark:text-red-400 text-base" />
              <span class="text-[9px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest">Vencidos</span>
            </div>
            <span class="text-[10px] font-black text-red-600 dark:text-red-400">{{ calendarioProximos.vencidos.length }}</span>
          </div>
          <p class="text-lg sm:text-2xl font-black text-red-700 dark:text-red-300">
            {{ formatearMoneda(calendarioProximos.vencidos.reduce((s, p) => s + (p.saldo_pendiente || p.monto_total || 0), 0)) }}
          </p>
          <p class="text-[9px] text-red-500 dark:text-red-400 mt-1 font-bold">
            {{ calendarioProximos.vencidos.length > 0 ? '¡Requiere atención!' : 'Al día ✓' }}
          </p>
        </button>

        <!-- VENCE HOY -->
        <button @click="abrirCalendarioDetalle('hoy')"
                class="text-left rounded-2xl p-3 sm:p-4 border-2 transition-all hover:shadow-md"
                :class="calendarioProximos.hoy.length > 0
                  ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-800 hover:border-orange-400'
                  : 'bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700'">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-1.5">
              <Icon icon="mdi:clock-alert" class="text-orange-600 dark:text-orange-400 text-base" />
              <span class="text-[9px] font-black text-orange-600 dark:text-orange-400 uppercase tracking-widest">Hoy</span>
            </div>
            <span class="text-[10px] font-black text-orange-600 dark:text-orange-400">{{ calendarioProximos.hoy.length }}</span>
          </div>
          <p class="text-lg sm:text-2xl font-black text-orange-700 dark:text-orange-300">
            {{ formatearMoneda(calendarioProximos.hoy.reduce((s, p) => s + (p.saldo_pendiente || p.monto_total || 0), 0)) }}
          </p>
          <p class="text-[9px] text-orange-500 dark:text-orange-400 mt-1 font-bold">
            {{ calendarioProximos.hoy.length > 0 ? '¡Vence hoy!' : 'Nada pendiente' }}
          </p>
        </button>

        <!-- 3 DÍAS -->
        <button @click="abrirCalendarioDetalle('tresDias')"
                class="text-left rounded-2xl p-3 sm:p-4 border-2 transition-all hover:shadow-md"
                :class="calendarioProximos.tresDias.length + calendarioProximos.manana.length > 0
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800 hover:border-amber-400'
                  : 'bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700'">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-1.5">
              <Icon icon="mdi:clock-outline" class="text-amber-600 dark:text-amber-400 text-base" />
              <span class="text-[9px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-widest">3 días</span>
            </div>
            <span class="text-[10px] font-black text-amber-600 dark:text-amber-400">
              {{ calendarioProximos.tresDias.length + calendarioProximos.manana.length }}
            </span>
          </div>
          <p class="text-lg sm:text-2xl font-black text-amber-700 dark:text-amber-300">
            {{ formatearMoneda([...calendarioProximos.tresDias, ...calendarioProximos.manana].reduce((s, p) => s + (p.saldo_pendiente || p.monto_total || 0), 0)) }}
          </p>
          <p class="text-[9px] text-amber-500 dark:text-amber-400 mt-1 font-bold">
            Próximos 3 días
          </p>
        </button>

        <!-- SEMANAL -->
        <button @click="abrirCalendarioDetalle('semana')"
                class="text-left rounded-2xl p-3 sm:p-4 border-2 transition-all hover:shadow-md"
                :class="calendarioProximos.semana.length > 0
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-800 hover:border-emerald-400'
                  : 'bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700'">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-1.5">
              <Icon icon="mdi:calendar-week" class="text-emerald-600 dark:text-emerald-400 text-base" />
              <span class="text-[9px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Semana</span>
            </div>
            <span class="text-[10px] font-black text-emerald-600 dark:text-emerald-400">{{ calendarioProximos.semana.length }}</span>
          </div>
          <p class="text-lg sm:text-2xl font-black text-emerald-700 dark:text-emerald-300">
            {{ formatearMoneda(calendarioProximos.semana.reduce((s, p) => s + (p.saldo_pendiente || p.monto_total || 0), 0)) }}
          </p>
          <p class="text-[9px] text-emerald-500 dark:text-emerald-400 mt-1 font-bold">
            Días 4 al 7
          </p>
        </button>

        <!-- ESTE MES -->
        <button @click="abrirCalendarioDetalle('esteMes')"
                class="col-span-2 lg:col-span-1 text-left rounded-2xl p-3 sm:p-4 border-2 transition-all hover:shadow-md"
                :class="calendarioProximos.esteMes.length > 0
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-800 hover:border-blue-400'
                  : 'bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700'">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-1.5">
              <Icon icon="mdi:calendar-month" class="text-blue-600 dark:text-blue-400 text-base" />
              <span class="text-[9px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">Este Mes</span>
            </div>
            <span class="text-[10px] font-black text-blue-600 dark:text-blue-400">{{ calendarioProximos.esteMes.length }}</span>
          </div>
          <p class="text-lg sm:text-2xl font-black text-blue-700 dark:text-blue-300">
            {{ formatearMoneda(calendarioProximos.esteMes.reduce((s, p) => s + (p.saldo_pendiente || p.monto_total || 0), 0)) }}
          </p>
          <p class="text-[9px] text-blue-500 dark:text-blue-400 mt-1 font-bold">
            Más de 7 días
          </p>
        </button>

      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-5">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1">Buscar</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-blue-500 transition-colors" />
            <input v-model="searchQuery" type="text" placeholder="Recibo, período, servicio..." class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base" />
          </div>
        </div>
        <div class="sm:col-span-4">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1">Estado</label>
          <select v-model="filterEstado" class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 rounded-xl font-black text-gray-800 dark:text-white outline-none appearance-none cursor-pointer text-sm sm:text-base">
            <option value="all">TODOS</option>
            <option value="pendiente">PENDIENTE</option>
            <option value="vencido">VENCIDO</option>
            <option value="pagado">PAGADO</option>
            <option value="parcial">PARCIAL</option>
            <option value="anulado">ANULADO</option>
          </select>
        </div>
        <div class="sm:col-span-3">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1">Contrato</label>
          <select v-model="filterContrato" class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 rounded-xl font-black text-gray-800 dark:text-white outline-none appearance-none cursor-pointer text-sm sm:text-base">
            <option value="all">TODOS</option>
            <option v-for="c in contratos" :key="c.idContratoServicio" :value="c.idContratoServicio">
              {{ c.alias || c.numero_contrato || 'Contrato ' + c.idContratoServicio }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Tabla / Tarjetas -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div v-if="loading" class="p-10 text-center">
        <Icon icon="mdi:loading" class="animate-spin text-4xl text-blue-500 mx-auto" />
        <p class="text-xs font-black text-gray-400 mt-3 tracking-widest">CARGANDO...</p>
      </div>

      <div v-else-if="filteredPagos.length === 0" class="p-16 text-center">
        <div class="flex flex-col items-center opacity-30 select-none">
          <Icon icon="mdi:receipt-text-outline" class="text-7xl mb-3" />
          <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay facturas</h4>
          <p class="text-sm text-gray-300 mt-1">Registra tu primera factura</p>
        </div>
      </div>

      <!-- DESKTOP: TABLA -->
      <div v-else class="hidden lg:block overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Servicio</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Recibo / Período</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Vencimiento</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">Monto</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-for="p in paginatedPagos" :key="p.idPagoServicio" class="hover:bg-blue-50/30 dark:hover:bg-gray-700/30 transition-all">
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                       :style="{ backgroundColor: getColorServicio(p) + '20' }">
                    <Icon :icon="getIconoServicio(p)" class="text-lg"
                          :style="{ color: getColorServicio(p) }" />
                  </div>
                  <div>
                    <p class="text-sm font-black text-gray-900 dark:text-white">{{ p.aliasContrato || p.nombreServicio || 'Sin alias' }}</p>
                    <p class="text-[10px] text-gray-500 dark:text-gray-400">{{ p.nombreServicio || p.tipoServicio }}</p>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4">
                <p class="text-xs font-black text-gray-700 dark:text-gray-300">{{ p.numero_recibo || 'Sin recibo' }}</p>
                <p class="text-[10px] text-gray-500">{{ formatearPeriodo(p.periodo) || 'Sin período' }}</p>
              </td>
              <td class="px-5 py-4">
                <p class="text-xs font-black text-gray-700 dark:text-gray-300">{{ formatearFecha(p.fecha_vencimiento) }}</p>
                <p v-if="p.estado === 'pendiente' || p.estado === 'parcial'" :class="[
                  'text-[10px] font-black',
                  (p.diasParaVencimiento || 0) < 0 ? 'text-red-600' :
                  (p.diasParaVencimiento || 0) <= 3 ? 'text-orange-600' :
                  'text-gray-500'
                ]">
                  {{ (p.diasParaVencimiento || 0) < 0 ? `Vencido hace ${Math.abs(p.diasParaVencimiento)}d` :
                     p.diasParaVencimiento === 0 ? '¡Vence HOY!' :
                     `Faltan ${p.diasParaVencimiento}d` }}
                </p>
              </td>
              <td class="px-5 py-4 text-right">
                <p class="text-sm font-black text-gray-900 dark:text-white">{{ formatearMoneda(p.monto_total) }}</p>
                <p v-if="p.saldo_pendiente > 0 && p.monto_pagado > 0" class="text-[10px] font-bold text-orange-600">
                  Debe: {{ formatearMoneda(p.saldo_pendiente) }}
                </p>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="getEstadoClass(p)">{{ p.estado }}</span>
              </td>
              <td class="px-5 py-4 text-right">
                <div class="flex items-center justify-end gap-1">
                  <button v-if="p.estado === 'pendiente' || p.estado === 'parcial'"
                          @click="openPagarModal(p)"
                          class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all"
                          title="Registrar Pago">
                    <Icon icon="mdi:cash-check" class="text-base" />
                  </button>
                  <button @click="descargarFactura(p)"
                          class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all"
                          title="Descargar / Imprimir">
                    <Icon icon="mdi:download" class="text-base" />
                  </button>
                  <button @click="openEditModal(p)" class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all" title="Editar">
                    <Icon icon="mdi:pencil" class="text-base" />
                  </button>
                  <button @click="abrirModalEliminar(p)" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all" title="Eliminar">
                    <Icon icon="mdi:delete" class="text-base" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- MÓVIL: TARJETAS -->
      <div v-if="!loading && filteredPagos.length > 0" class="lg:hidden p-2 sm:p-3 space-y-3">
        <div v-for="p in paginatedPagos" :key="'card-' + p.idPagoServicio"
             class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-all"
             :class="{
               'border-l-4 border-l-red-500': (p.estado === 'pendiente' || p.estado === 'parcial') && (p.diasParaVencimiento || 0) < 0,
               'border-l-4 border-l-orange-500': (p.estado === 'pendiente' || p.estado === 'parcial') && (p.diasParaVencimiento || 0) === 0,
               'border-l-4 border-l-amber-500': (p.estado === 'pendiente' || p.estado === 'parcial') && (p.diasParaVencimiento || 0) > 0 && (p.diasParaVencimiento || 0) <= 3,
               'border-l-4 border-l-emerald-500': p.estado === 'pagado'
             }">

          <div class="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-3 min-w-0 flex-1">
              <div class="w-12 h-12 flex-shrink-0 rounded-xl flex items-center justify-center shadow-sm"
                   :style="{ backgroundColor: getColorServicio(p) + '20' }">
                <Icon :icon="getIconoServicio(p)" class="text-xl"
                      :style="{ color: getColorServicio(p) }" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-black text-gray-900 dark:text-white truncate">
                  {{ p.aliasContrato || p.nombreServicio || 'Sin alias' }}
                </p>
                <p class="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                  {{ p.nombreServicio || p.tipoServicio }}
                </p>
              </div>
            </div>
            <span :class="getEstadoClass(p)">{{ p.estado }}</span>
          </div>

          <div class="grid grid-cols-2 gap-3 mb-3">
            <div>
              <p class="text-[9px] font-black text-gray-400 tracking-widest uppercase">N° Recibo</p>
              <p class="text-xs font-bold text-gray-800 dark:text-white truncate">
                {{ p.numero_recibo || '—' }}
              </p>
            </div>
            <div>
              <p class="text-[9px] font-black text-gray-400 tracking-widest uppercase">Período</p>
              <p class="text-xs font-bold text-gray-800 dark:text-white truncate">
                {{ formatearPeriodo(p.periodo) || '—' }}
              </p>
            </div>
            <div>
              <p class="text-[9px] font-black text-gray-400 tracking-widest uppercase">Vencimiento</p>
              <p class="text-xs font-bold text-gray-800 dark:text-white">
                {{ formatearFecha(p.fecha_vencimiento) }}
              </p>
            </div>
            <div>
              <p class="text-[9px] font-black text-gray-400 tracking-widest uppercase">Total</p>
              <p class="text-xs font-black text-gray-900 dark:text-white">
                {{ formatearMoneda(p.monto_total) }}
              </p>
            </div>
          </div>

          <div v-if="(p.estado === 'pendiente' || p.estado === 'parcial') && (p.diasParaVencimiento || 0) < 0"
               class="mb-3 p-2.5 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center gap-2">
            <Icon icon="mdi:alert-circle" class="text-red-600 text-base flex-shrink-0" />
            <span class="text-[11px] font-black text-red-600 dark:text-red-400">
              Vencido hace {{ Math.abs(p.diasParaVencimiento) }} día(s)
            </span>
          </div>
          <div v-else-if="(p.estado === 'pendiente' || p.estado === 'parcial') && (p.diasParaVencimiento || 0) === 0"
               class="mb-3 p-2.5 bg-orange-50 dark:bg-orange-900/20 rounded-xl flex items-center gap-2">
            <Icon icon="mdi:clock-alert" class="text-orange-600 text-base flex-shrink-0" />
            <span class="text-[11px] font-black text-orange-600 dark:text-orange-400">¡Vence HOY!</span>
          </div>
          <div v-else-if="(p.estado === 'pendiente' || p.estado === 'parcial') && (p.diasParaVencimiento || 0) <= 3"
               class="mb-3 p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-xl flex items-center gap-2">
            <Icon icon="mdi:clock-outline" class="text-amber-600 text-base flex-shrink-0" />
            <span class="text-[11px] font-black text-amber-600 dark:text-amber-400">
              Faltan {{ p.diasParaVencimiento }} día(s)
            </span>
          </div>

          <div v-if="p.saldo_pendiente > 0 && p.monto_pagado > 0"
               class="mb-3 p-2.5 bg-orange-50 dark:bg-orange-900/20 rounded-xl flex items-center justify-between">
            <span class="text-[10px] font-black text-orange-600 dark:text-orange-400 uppercase tracking-widest">Saldo pendiente</span>
            <span class="text-sm font-black text-orange-700 dark:text-orange-300">{{ formatearMoneda(p.saldo_pendiente) }}</span>
          </div>

          <div class="flex gap-2 pt-3 border-t border-gray-100 dark:border-gray-700 flex-wrap">
            <button v-if="p.estado === 'pendiente' || p.estado === 'parcial'"
                    @click="openPagarModal(p)"
                    class="flex-1 min-w-[80px] py-2.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl font-black text-[10px] tracking-widest hover:bg-emerald-500 hover:text-white transition-all flex items-center justify-center gap-1.5">
              <Icon icon="mdi:cash-check" class="text-sm" />
              Pagar
            </button>
            <button @click="descargarFactura(p)"
                    class="flex-1 min-w-[80px] py-2.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl font-black text-[10px] tracking-widest hover:bg-blue-500 hover:text-white transition-all flex items-center justify-center gap-1.5">
              <Icon icon="mdi:download" class="text-sm" />
              PDF
            </button>
            <button @click="openEditModal(p)"
                    class="flex-1 min-w-[80px] py-2.5 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-xl font-black text-[10px] tracking-widest hover:bg-amber-500 hover:text-white transition-all flex items-center justify-center gap-1.5">
              <Icon icon="mdi:pencil" class="text-sm" />
              Editar
            </button>
            <button @click="abrirModalEliminar(p)"
                    class="p-2.5 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 rounded-xl hover:bg-rose-500 hover:text-white transition-all">
              <Icon icon="mdi:delete" class="text-sm" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- PAGINACIÓN -->
    <div v-if="filteredPagos.length > 0 && !loading"
         class="mt-4 sm:mt-5 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 px-3 sm:px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
      <div class="flex items-center gap-3 flex-wrap justify-center sm:justify-start">
        <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
          Mostrando <span class="text-blue-600">{{ startItem }}</span> - <span class="text-blue-600">{{ endItem }}</span>
          de <span class="text-blue-600">{{ filteredPagos.length }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-[10px] font-black text-gray-400 uppercase tracking-widest hidden sm:inline">Mostrar:</span>
          <select v-model.number="itemsPerPage" @change="currentPage = 1"
                  class="px-2 py-1 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 rounded-lg text-[11px] font-black text-gray-700 dark:text-white outline-none cursor-pointer">
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
      </div>
      <div class="flex items-center gap-1 flex-wrap justify-center">
        <button @click="currentPage = 1" :disabled="currentPage === 1"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === 1
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']">
          <Icon icon="mdi:page-first" class="text-base sm:text-lg" />
        </button>
        <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === 1
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']">
          <Icon icon="mdi:chevron-left" class="text-base sm:text-lg" />
        </button>
        <div class="hidden sm:flex items-center gap-0.5 mx-1">
          <button v-for="page in visiblePages" :key="page" @click="currentPage = page"
                  :class="['min-w-[34px] h-9 rounded-lg text-[11px] font-black transition-all flex items-center justify-center',
                    currentPage === page
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/30'
                      : 'text-gray-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 border border-transparent hover:border-blue-200 dark:hover:border-blue-800']">
            {{ page }}
          </button>
        </div>
        <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2 whitespace-nowrap">
          Pág. {{ currentPage }} / {{ totalPages }}
        </span>
        <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === totalPages
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']">
          <Icon icon="mdi:chevron-right" class="text-base sm:text-lg" />
        </button>
        <button @click="currentPage = totalPages" :disabled="currentPage === totalPages"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === totalPages
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']">
          <Icon icon="mdi:page-last" class="text-base sm:text-lg" />
        </button>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL CREAR / EDITAR FACTURA -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showPagoModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9998] overflow-y-auto" @click.self="closePagoModal">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden animate-zoomIn my-auto flex flex-col">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-4 flex items-center gap-3 flex-shrink-0">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editing ? 'mdi:pencil-box' : 'mdi:file-plus'" class="text-white text-xl" />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-black text-white truncate">{{ editing ? 'Editar Factura' : 'Nueva Factura a Pagar' }}</h3>
              <p class="text-[10px] text-white/70 font-bold truncate">{{ editing ? 'Modifica los datos' : 'Registra una factura pendiente' }}</p>
            </div>
            <button @click="closePagoModal" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all flex-shrink-0">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-6 overflow-y-auto flex-1">
            <form @submit.prevent="savePago" class="space-y-4">

              <!-- BUSCADOR DE CONTRATO -->
              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                  Contrato * <span class="text-gray-300 normal-case tracking-normal font-medium">(busca por alias, N° contrato o servicio)</span>
                </label>

                <div v-if="contratoSeleccionado" class="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-xl border-2 border-blue-500">
                  <div class="w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center"
                       :style="{ backgroundColor: getColorServicio(contratoSeleccionado) + '20' }">
                    <Icon :icon="getIconoServicio(contratoSeleccionado)" class="text-lg"
                          :style="{ color: getColorServicio(contratoSeleccionado) }" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-black text-gray-900 dark:text-white truncate">
                      {{ contratoSeleccionado.alias || contratoSeleccionado.numero_contrato || 'Sin alias' }}
                    </p>
                    <p class="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                      {{ contratoSeleccionado.nombreServicioPublico }} • {{ contratoSeleccionado.proveedor || 'Sin proveedor' }}
                    </p>
                  </div>
                  <button type="button" @click="limpiarContrato"
                          class="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/30 text-gray-500 hover:text-rose-600 transition-all flex-shrink-0"
                          title="Cambiar contrato">
                    <Icon icon="mdi:close" class="text-sm" />
                  </button>
                </div>

                <div v-else class="relative">
                  <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                  <input v-model="busquedaContrato" @focus="mostrarListaContratos = true" @blur="cerrarListaContratosDelayed"
                         type="text" placeholder="Escribe para buscar contrato..."
                         class="w-full pl-10 pr-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none" />

                  <div v-if="mostrarListaContratos && contratosFiltrados.length > 0"
                       class="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto">
                    <button v-for="c in contratosFiltrados" :key="c.idContratoServicio" type="button"
                            @mousedown.prevent="seleccionarContrato(c)"
                            class="w-full text-left p-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all border-b border-gray-100 dark:border-gray-700 last:border-0 flex items-center gap-3">
                      <div class="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center"
                           :style="{ backgroundColor: getColorServicio(c) + '20' }">
                        <Icon :icon="getIconoServicio(c)" class="text-sm" :style="{ color: getColorServicio(c) }" />
                      </div>
                      <div class="min-w-0 flex-1">
                        <p class="text-xs font-black text-gray-900 dark:text-white truncate">
                          {{ c.alias || c.numero_contrato || 'Sin alias' }}
                        </p>
                        <p class="text-[10px] text-gray-500 truncate">
                          {{ c.nombreServicioPublico }} • {{ c.numero_contrato || 'Sin N°' }}
                        </p>
                      </div>
                    </button>
                  </div>

                  <div v-if="mostrarListaContratos && busquedaContrato && contratosFiltrados.length === 0"
                       class="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl shadow-xl z-50 p-4 text-center">
                    <Icon icon="mdi:file-search-outline" class="text-2xl text-gray-300 mx-auto mb-1" />
                    <p class="text-xs font-black text-gray-400">No se encontró contrato</p>
                  </div>
                </div>
              </div>

              <!-- Info de ayuda si hay contrato seleccionado -->
              <div v-if="contratoSeleccionado && contratoSeleccionado.monto_estimado > 0"
                   class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <Icon icon="mdi:information-outline" class="text-blue-600 text-base" />
                  <span class="text-[11px] font-bold text-blue-700 dark:text-blue-300">Monto estimado del contrato</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-sm font-black text-blue-700 dark:text-blue-300">{{ formatearMoneda(contratoSeleccionado.monto_estimado) }}</span>
                  <button v-if="!editing && formPago.monto_original !== contratoSeleccionado.monto_estimado"
                          type="button"
                          @click="formPago.monto_original = contratoSeleccionado.monto_estimado"
                          class="px-2 py-1 bg-blue-600 text-white rounded-lg font-black text-[9px] tracking-widest hover:bg-blue-700 transition-all">
                    USAR
                  </button>
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">N° Recibo</label>
                  <input v-model="formPago.numero_recibo" type="text" maxlength="50" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none" placeholder="Ej: 00123" />
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Período</label>
                  <input v-model="formPago.periodo" type="text" maxlength="20" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none" placeholder="Ej: 2025-01" />
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Monto Original USD *</label>
                  <input v-model.number="formPago.monto_original" type="number" step="0.01" min="0.01" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none" />
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Mora USD</label>
                  <input v-model.number="formPago.monto_mora" type="number" step="0.01" min="0" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none" />
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Fecha Emisión</label>
                  <input v-model="formPago.fecha_emision" type="date" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none" />
                </div>

                <!-- FECHA VENCIMIENTO - OBLIGATORIA -->
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                    Fecha Vencimiento * <span class="text-red-500">(obligatoria)</span>
                  </label>
                  <input v-model="formPago.fecha_vencimiento" type="date" required
                         :class="[
                           'w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none',
                           formPago.fecha_vencimiento
                             ? 'border-gray-200 dark:border-gray-600 focus:border-blue-500'
                             : 'border-red-300 dark:border-red-700 focus:border-red-500'
                         ]" />
                  <p v-if="!formPago.fecha_vencimiento" class="text-[10px] text-red-500 mt-1 font-bold">
                    ⚠️ Este campo es obligatorio
                  </p>
                </div>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Observaciones</label>
                <textarea v-model="formPago.observaciones" rows="2" maxlength="300" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none resize-none"></textarea>
              </div>

              <div class="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="closePagoModal" class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest order-2 sm:order-1">Cancelar</button>
                <button type="submit" :disabled="saving" class="w-full sm:flex-[2] py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50 order-1 sm:order-2">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{ 'animate-spin': saving }" />
                  {{ saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear Factura') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL REGISTRAR PAGO -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showPagarModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] overflow-y-auto" @click.self="closePagarModal">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-hidden animate-zoomIn my-auto flex flex-col">
          <div class="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 sm:px-6 py-4 flex items-center gap-3 flex-shrink-0">
            <div class="p-2 bg-white/20 rounded-xl flex-shrink-0">
              <Icon icon="mdi:cash-check" class="text-white text-xl" />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-black text-white">Registrar Pago</h3>
              <p class="text-[10px] text-white/70 font-bold truncate">{{ pagoTarget?.aliasContrato || pagoTarget?.nombreServicio }}</p>
            </div>
            <button @click="closePagarModal" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg flex-shrink-0">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-6 overflow-y-auto flex-1">
            <div class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-4">
              <div class="flex justify-between text-xs mb-1">
                <span class="font-black text-gray-500">Total factura:</span>
                <span class="font-black text-gray-800 dark:text-white">{{ formatearMoneda(pagoTarget?.monto_total) }}</span>
              </div>
              <div class="flex justify-between text-xs mb-1">
                <span class="font-black text-gray-500">Ya pagado:</span>
                <span class="font-black text-emerald-600">{{ formatearMoneda(pagoTarget?.monto_pagado) }}</span>
              </div>
              <div class="flex justify-between text-sm pt-2 border-t border-blue-200">
                <span class="font-black text-gray-800 dark:text-white">Saldo pendiente:</span>
                <span class="font-black text-red-600">{{ formatearMoneda(pagoTarget?.saldo_pendiente) }}</span>
              </div>
            </div>

            <form @submit.prevent="ejecutarPago" class="space-y-3">
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Monto a Pagar USD *</label>
                <input v-model.number="formPagar.monto_pagado" @input="recalcularMontoBs" type="number" step="0.01" min="0.01" required class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-black text-gray-800 dark:text-white outline-none" />
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <div class="flex items-center justify-between mb-2">
                  <label class="text-[10px] font-black text-gray-400 tracking-widest">Tasa Bs/USD</label>
                  <button type="button" @click="traerTasaActual" :disabled="cargandoTasa"
                          class="px-2.5 py-1 bg-blue-600 text-white rounded-lg font-black text-[9px] tracking-widest hover:bg-blue-700 transition-all flex items-center gap-1 disabled:opacity-50">
                    <Icon :icon="cargandoTasa ? 'mdi:loading' : 'mdi:refresh'" :class="{ 'animate-spin': cargandoTasa }" class="text-xs" />
                    {{ cargandoTasa ? 'Cargando...' : 'Traer Tasa Actual' }}
                  </button>
                </div>
                <input v-model.number="formPagar.tasa_bs_usd" @input="recalcularMontoBs" type="number" step="0.01" placeholder="Ej: 36.50"
                       class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-black text-gray-800 dark:text-white outline-none" />
                <p v-if="tasaActualInfo" class="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
                  <Icon icon="mdi:check-circle" class="text-emerald-500 text-xs" />
                  Fuente: <strong>{{ tasaActualInfo.fuente }}</strong> — {{ formatearFecha(tasaActualInfo.fecha) }}
                </p>
              </div>

              <div class="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <Icon icon="mdi:calculator-variant" class="text-emerald-600 text-lg" />
                    <span class="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Monto en Bs (calculado)</span>
                  </div>
                  <span class="text-lg font-black text-emerald-700 dark:text-emerald-300">
                    Bs. {{ formatearBs(formPagar.monto_bs) }}
                  </span>
                </div>
                <p class="text-[10px] text-gray-500 mt-1">
                  {{ formatearMoneda(formPagar.monto_pagado) }} × {{ formPagar.tasa_bs_usd || 0 }} Bs/USD
                </p>
              </div>

              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Método de Pago *</label>
                <select v-model="formPagar.metodo_pago" required class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none cursor-pointer">
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="pago_movil">Pago Móvil</option>
                  <option value="zelle">Zelle</option>
                  <option value="punto">Punto de Venta</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Referencia</label>
                <input v-model="formPagar.referencia" type="text" maxlength="100" class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none" />
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Observaciones</label>
                <textarea v-model="formPagar.observaciones" rows="2" maxlength="200" class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none resize-none"></textarea>
              </div>

              <div class="flex flex-col sm:flex-row gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="closePagarModal" class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest order-2 sm:order-1">Cancelar</button>
                <button type="submit" :disabled="pagando" class="w-full sm:flex-[2] py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50 order-1 sm:order-2">
                  <Icon :icon="pagando ? 'mdi:loading' : 'mdi:cash-check'" :class="{ 'animate-spin': pagando }" />
                  {{ pagando ? 'Procesando...' : 'Confirmar Pago' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL POST-PAGO: Crear siguiente factura -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showPostPagoModal"
           class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[10004] overflow-y-auto"
           @click.self="cerrarPostPagoModal">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-hidden animate-zoomIn my-auto flex flex-col">

          <div class="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 flex items-center gap-3 flex-shrink-0">
            <div class="p-2 bg-white/20 rounded-xl flex-shrink-0">
              <Icon icon="mdi:check-circle" class="text-white text-xl" />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-black text-white">¡Pago Registrado!</h3>
              <p class="text-[10px] text-white/80 font-bold truncate">
                {{ postPagoTarget?.aliasContrato || postPagoTarget?.nombreServicio }}
              </p>
            </div>
            <button @click="cerrarPostPagoModal" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg flex-shrink-0">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>

          <div class="p-6 overflow-y-auto flex-1">

            <div class="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 mb-5 flex items-start gap-3">
              <Icon icon="mdi:information-outline" class="text-emerald-600 text-xl flex-shrink-0 mt-0.5" />
              <div>
                <p class="text-sm font-black text-emerald-700 dark:text-emerald-300 mb-1">
                  Este es un servicio {{ postPagoTarget?.periodicidad }}
                </p>
                <p class="text-xs text-gray-600 dark:text-gray-400">
                  ¿Quieres crear la factura del próximo período ahora mismo?
                </p>
              </div>
            </div>

            <div class="space-y-4">

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Contrato</label>
                <p class="text-sm font-black text-gray-900 dark:text-white">
                  {{ postPagoTarget?.contratoAlias || postPagoTarget?.aliasContrato }}
                </p>
                <p class="text-[10px] text-gray-500">{{ postPagoTarget?.nombreServicio }}</p>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                  Período de la nueva factura *
                </label>
                <input v-model="postPagoPeriodo" type="month" required
                       class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-black text-gray-800 dark:text-white outline-none" />
                <p class="text-[10px] text-gray-500 mt-1">
                  Calculado automáticamente según periodicidad: <strong>{{ postPagoTarget?.periodicidad }}</strong>
                </p>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                  Monto estimado USD
                </label>
                <input v-model.number="postPagoTarget.montoEstimado" type="number" step="0.01" min="0"
                       class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-black text-gray-800 dark:text-white outline-none" />
                <p class="text-[10px] text-gray-500 mt-1">Precargado del contrato, puedes ajustarlo si cambió</p>
              </div>

              <div class="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border-2 border-amber-300 dark:border-amber-700">
                <label class="block text-[10px] font-black text-amber-600 dark:text-amber-400 tracking-widest mb-2 flex items-center gap-1">
                  <Icon icon="mdi:alert-circle" class="text-sm" />
                  Fecha de Vencimiento * (obligatoria)
                </label>
                <input v-model="postPagoFechaVencimiento" type="date" required
                       class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-amber-400 focus:border-amber-500 rounded-xl text-sm font-black text-gray-800 dark:text-white outline-none" />
                <p class="text-[10px] text-amber-600 dark:text-amber-400 mt-1 font-bold">
                  ⚠️ Debes ingresar la fecha exacta de vencimiento de la nueva factura
                </p>
              </div>

            </div>
          </div>

          <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button @click="cerrarPostPagoModal"
                    class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest order-2 sm:order-1">
              Ahora no
            </button>
            <button @click="crearSiguienteFactura"
                    :disabled="postPagoCreando || !postPagoFechaVencimiento"
                    class="w-full sm:flex-[2] py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50 order-1 sm:order-2">
              <Icon :icon="postPagoCreando ? 'mdi:loading' : 'mdi:file-plus'" :class="{ 'animate-spin': postPagoCreando }" />
              {{ postPagoCreando ? 'Creando...' : 'Crear Siguiente Factura' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- 🆕 MODAL DETALLE CALENDARIO -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showCalendarioDetalle" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[10005] overflow-y-auto" @click.self="showCalendarioDetalle = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-hidden animate-zoomIn my-auto flex flex-col">

          <div :class="[
            'px-6 py-4 flex items-center gap-3 flex-shrink-0',
            getCalendarioHeaderClass()
          ]">
            <div class="p-2 bg-white/20 rounded-xl flex-shrink-0">
              <Icon :icon="getCalendarioIcon()" class="text-white text-xl" />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-black text-white">{{ getCalendarioTitulo() }}</h3>
              <p class="text-[10px] text-white/80 truncate">
                {{ calendarioDetalle.length }} factura(s) — {{ formatearMoneda(calendarioDetalle.reduce((s, p) => s + (p.saldo_pendiente || p.monto_total || 0), 0)) }}
              </p>
            </div>
            <button @click="showCalendarioDetalle = false" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg flex-shrink-0">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>

          <div class="p-4 sm:p-6 overflow-y-auto flex-1">
            <div v-if="calendarioDetalle.length === 0" class="text-center py-10">
              <Icon icon="mdi:check-circle" class="text-6xl text-emerald-300 mx-auto mb-3" />
              <p class="text-sm font-black text-gray-400">¡Todo al día! No hay facturas en este rango</p>
            </div>

            <div v-else class="space-y-2">
              <div v-for="p in calendarioDetalle" :key="'cal-' + p.idPagoServicio"
                   class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900/30 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all cursor-pointer"
                   @click="verDetalleFactura(p)">
                <div class="w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center shadow-sm"
                     :style="{ backgroundColor: getColorServicio(p) + '20' }">
                  <Icon :icon="getIconoServicio(p)" class="text-lg"
                        :style="{ color: getColorServicio(p) }" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-black text-gray-900 dark:text-white truncate">
                    {{ p.aliasContrato || p.nombreServicio }}
                  </p>
                  <div class="flex items-center gap-2 text-[10px] text-gray-500">
                    <span>{{ formatearPeriodo(p.periodo) }}</span>
                    <span>•</span>
                    <span :class="{
                      'text-red-600 font-black': p.diffDias < 0,
                      'text-orange-600 font-black': p.diffDias === 0,
                      'text-amber-600': p.diffDias > 0 && p.diffDias <= 3
                    }">
                      {{ p.diffDias < 0 ? `Vencido hace ${Math.abs(p.diffDias)}d` :
                         p.diffDias === 0 ? '¡Vence HOY!' :
                         `En ${p.diffDias} día(s)` }}
                    </span>
                  </div>
                </div>
                <div class="text-right flex-shrink-0">
                  <p class="text-sm font-black text-gray-900 dark:text-white">{{ formatearMoneda(p.monto_total) }}</p>
                  <button v-if="p.estado === 'pendiente' || p.estado === 'parcial'"
                          @click.stop="openPagarModal(p); showCalendarioDetalle = false;"
                          class="text-[10px] font-black text-emerald-600 hover:text-emerald-700 mt-0.5">
                    Pagar →
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-end flex-shrink-0">
            <button @click="showCalendarioDetalle = false" class="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL CONFIRMAR ELIMINAR -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[10002] overflow-y-auto" @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn my-auto">
          <div class="px-6 py-4 bg-gradient-to-r from-red-600 to-pink-600">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl"><Icon icon="mdi:alert-circle" class="text-white text-xl" /></div>
              <div>
                <h3 class="text-sm font-black text-white">Eliminar Factura</h3>
                <p class="text-[10px] text-white/80">Esta acción no se puede deshacer</p>
              </div>
            </div>
          </div>
          <div class="p-6">
            <p class="text-sm text-gray-700 dark:text-gray-300">¿Eliminar la factura <strong>{{ confirmTarget?.numero_recibo || confirmTarget?.periodo }}</strong>?</p>
          </div>
          <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex gap-3">
            <button @click="cancelarConfirmacion" class="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest">Cancelar</button>
            <button @click="ejecutarEliminar" :disabled="confirmLoading" class="flex-[2] py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2">
              <Icon :icon="confirmLoading ? 'mdi:loading' : 'mdi:delete'" :class="{ 'animate-spin': confirmLoading }" />
              {{ confirmLoading ? 'Eliminando...' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { useToast } from 'vue-toastification';

import {
  obtenerPagos,
  crearPago,
  actualizarPago,
  eliminarPago,
  registrarPago,
  formatearMoneda,
  formatearFecha,
  formatearPeriodo
} from '@/api/pagoServicio.js';

import { obtenerContratos } from '@/api/contratoServicio.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { getColorServicio, getIconoServicio } from '@/api/servicioPublico.js';

export default {
  name: 'PagoServicioPublico',
  components: { Icon },
  data() {
    return {
      pagos: [],
      contratos: [],
      loading: false,
      saving: false,
      pagando: false,
      searchQuery: '',
      filterEstado: 'all',
      filterContrato: 'all',

      // Paginación
      currentPage: 1,
      itemsPerPage: 10,

      // Modal crear/editar
      showPagoModal: false,
      editing: null,
      formPago: {
        idContratoServicio: 0,
        numero_recibo: '',
        periodo: '',
        fecha_emision: '',
        fecha_vencimiento: '',
        monto_original: 0,
        monto_mora: 0,
        observaciones: ''
      },

      // Búsqueda de contrato
      busquedaContrato: '',
      mostrarListaContratos: false,
      contratoSeleccionado: null,

      // Modal pagar
      showPagarModal: false,
      pagoTarget: null,
      formPagar: {
        monto_pagado: 0,
        metodo_pago: 'efectivo',
        referencia: '',
        tasa_bs_usd: null,
        monto_bs: null,
        observaciones: ''
      },
      cargandoTasa: false,
      tasaActualInfo: null,

      // Modal post-pago
      showPostPagoModal: false,
      postPagoTarget: null,
      postPagoPeriodo: '',
      postPagoFechaVencimiento: '',
      postPagoCreando: false,

      // Calendario
      showCalendarioDetalle: false,
      calendarioDetalle: [],
      calendarioTipoActual: '',

      // Confirm eliminar
      showConfirmModal: false,
      confirmTarget: null,
      confirmLoading: false
    };
  },
  computed: {
    filteredPagos() {
      let list = [...this.pagos];
      const q = this.searchQuery.trim().toLowerCase();
      if (q) {
        list = list.filter(p =>
          (p.numero_recibo || '').toLowerCase().includes(q) ||
          (p.periodo || '').toLowerCase().includes(q) ||
          (p.aliasContrato || '').toLowerCase().includes(q) ||
          (p.nombreServicio || '').toLowerCase().includes(q)
        );
      }
      if (this.filterEstado !== 'all') list = list.filter(p => p.estado === this.filterEstado);
      if (this.filterContrato !== 'all') list = list.filter(p => p.idContratoServicio === this.filterContrato);

      // ORDENAR: Pendientes primero, por fecha de vencimiento (más vencidos primero)
      const prioridad = (p) => {
        if (p.estado === 'pendiente' || p.estado === 'parcial') return 1;
        if (p.estado === 'pagado') return 2;
        if (p.estado === 'anulado') return 3;
        return 4;
      };

      list.sort((a, b) => {
        const pa = prioridad(a);
        const pb = prioridad(b);
        if (pa !== pb) return pa - pb;

        if (pa === 1) {
          const fa = new Date(a.fecha_vencimiento).getTime();
          const fb = new Date(b.fecha_vencimiento).getTime();
          return fa - fb;
        }

        if (pa === 2) {
          const fa = new Date(a.fecha_pago || a.fecha_vencimiento).getTime();
          const fb = new Date(b.fecha_pago || b.fecha_vencimiento).getTime();
          return fb - fa;
        }

        return new Date(b.fecha_vencimiento) - new Date(a.fecha_vencimiento);
      });

      return list;
    },

    contratosFiltrados() {
      const q = this.busquedaContrato.trim().toLowerCase();
      if (!q) return this.contratos.slice(0, 10);
      return this.contratos.filter(c =>
        (c.alias || '').toLowerCase().includes(q) ||
        (c.numero_contrato || '').toLowerCase().includes(q) ||
        (c.numero_medidor || '').toLowerCase().includes(q) ||
        (c.proveedor || '').toLowerCase().includes(q) ||
        (c.nombreServicioPublico || '').toLowerCase().includes(q)
      ).slice(0, 15);
    },

    stats() {
      const list = this.pagos;
      return {
        pendientes: list.filter(p => p.estado === 'pendiente').length,
        vencidos: list.filter(p => p.estado === 'pendiente' && new Date(p.fecha_vencimiento) < new Date()).length,
        pagados: list.filter(p => p.estado === 'pagado').length,
        montoPendiente: list.filter(p => p.estado === 'pendiente' || p.estado === 'parcial')
          .reduce((s, p) => s + (p.saldo_pendiente || 0), 0)
      };
    },

    // ============================================
    // 🆕 CALENDARIO
    // ============================================
    calendarioProximos() {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      const pendientes = this.pagos.filter(p =>
        p.estado === 'pendiente' || p.estado === 'parcial'
      );

      const hoyLista = [];
      const mananaLista = [];
      const tresDiasLista = [];
      const semanaLista = [];
      const esteMesLista = [];
      const vencidosLista = [];

      pendientes.forEach(p => {
        const venc = new Date(p.fecha_vencimiento);
        venc.setHours(0, 0, 0, 0);
        const diffDias = Math.ceil((venc - hoy) / (1000 * 60 * 60 * 24));

        if (diffDias < 0) vencidosLista.push({ ...p, diffDias });
        else if (diffDias === 0) hoyLista.push({ ...p, diffDias });
        else if (diffDias === 1) mananaLista.push({ ...p, diffDias });
        else if (diffDias <= 3) tresDiasLista.push({ ...p, diffDias });
        else if (diffDias <= 7) semanaLista.push({ ...p, diffDias });
        else esteMesLista.push({ ...p, diffDias });
      });

      const ordenar = (arr) => arr.sort((a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento));

      return {
        vencidos: ordenar(vencidosLista),
        hoy: ordenar(hoyLista),
        manana: ordenar(mananaLista),
        tresDias: ordenar(tresDiasLista),
        semana: ordenar(semanaLista),
        esteMes: ordenar(esteMesLista)
      };
    },

    totalProximos7Dias() {
      const c = this.calendarioProximos;
      return [...c.hoy, ...c.manana, ...c.tresDias, ...c.semana]
        .reduce((sum, p) => sum + (p.saldo_pendiente || p.monto_total || 0), 0);
    },

    totalPendientes() {
      const c = this.calendarioProximos;
      return c.vencidos.length + c.hoy.length + c.manana.length + c.tresDias.length + c.semana.length + c.esteMes.length;
    },

    paginatedPagos() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredPagos.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.ceil(this.filteredPagos.length / this.itemsPerPage) || 1;
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    startItem() {
      if (this.filteredPagos.length === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredPagos.length);
    }
  },
  mounted() {
    this.cargarTodo();
  },
  watch: {
    searchQuery() { this.currentPage = 1; },
    filterEstado() { this.currentPage = 1; },
    filterContrato() { this.currentPage = 1; }
  },
  methods: {
    formatearMoneda,
    formatearFecha,
    formatearPeriodo,
    getColorServicio,
    getIconoServicio,

    formatearBs(valor) {
      if (!valor && valor !== 0) return '0,00';
      return new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(valor);
    },

    showSuccess(mensaje, titulo = '¡Listo!') {
      useToast().success(`${titulo} — ${mensaje}`, { timeout: 3000, position: 'top-right' });
    },
    showError(mensaje, titulo = 'Error') {
      useToast().error(`${titulo} — ${mensaje}`, { timeout: 4000, position: 'top-right' });
    },
    showWarning(mensaje, titulo = 'Atención') {
      useToast().warning(`${titulo} — ${mensaje}`, { timeout: 3500, position: 'top-right' });
    },
    showInfo(mensaje) {
      useToast().info(mensaje, { timeout: 3000, position: 'top-right' });
    },

    async cargarTodo() {
      this.loading = true;
      try {
        const [p, c] = await Promise.all([obtenerPagos(), obtenerContratos()]);
        if (p.success) this.pagos = p.data || [];
        else this.showError(p.error || 'No se pudieron cargar los pagos');

        if (c.success) this.contratos = (c.data || []).filter(x => x.estado === 'activo');
        else this.showError(c.error || 'No se pudieron cargar los contratos');
      } catch (e) {
        console.error(e);
        this.showError('Error al cargar los datos');
      } finally {
        this.loading = false;
      }
    },

    getEstadoClass(p) {
      const base = 'px-3 py-1 inline-flex text-[10px] font-black uppercase tracking-widest rounded-lg border ';
      if (p.estado === 'pagado') return base + 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800';
      if (p.estado === 'parcial') return base + 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800';
      if (p.estado === 'anulado') return base + 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600';
      const vencido = new Date(p.fecha_vencimiento) < new Date();
      if (vencido) return base + 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
      return base + 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800';
    },

    // ============================================
    // CALENDARIO
    // ============================================
    abrirCalendarioDetalle(tipo) {
      const c = this.calendarioProximos;
      this.calendarioTipoActual = tipo;

      switch (tipo) {
        case 'vencidos': this.calendarioDetalle = c.vencidos; break;
        case 'hoy': this.calendarioDetalle = c.hoy; break;
        case 'tresDias': this.calendarioDetalle = [...c.manana, ...c.tresDias]; break;
        case 'semana': this.calendarioDetalle = c.semana; break;
        case 'esteMes': this.calendarioDetalle = c.esteMes; break;
        default: this.calendarioDetalle = [];
      }
      this.showCalendarioDetalle = true;
    },

    getCalendarioTitulo() {
      const titulos = {
        vencidos: 'Facturas Vencidas',
        hoy: 'Facturas que Vencen HOY',
        tresDias: 'Próximos 3 Días',
        semana: 'Esta Semana (días 4-7)',
        esteMes: 'Este Mes (más de 7 días)'
      };
      return titulos[this.calendarioTipoActual] || 'Detalle';
    },

    getCalendarioIcon() {
      const iconos = {
        vencidos: 'mdi:alert-circle',
        hoy: 'mdi:clock-alert',
        tresDias: 'mdi:clock-outline',
        semana: 'mdi:calendar-week',
        esteMes: 'mdi:calendar-month'
      };
      return iconos[this.calendarioTipoActual] || 'mdi:calendar';
    },

    getCalendarioHeaderClass() {
      const clases = {
        vencidos: 'bg-gradient-to-r from-red-600 to-rose-600',
        hoy: 'bg-gradient-to-r from-orange-600 to-amber-600',
        tresDias: 'bg-gradient-to-r from-amber-500 to-yellow-600',
        semana: 'bg-gradient-to-r from-emerald-600 to-teal-600',
        esteMes: 'bg-gradient-to-r from-blue-600 to-indigo-600'
      };
      return clases[this.calendarioTipoActual] || 'bg-gradient-to-r from-blue-600 to-indigo-600';
    },

    verDetalleFactura(p) {
      this.showCalendarioDetalle = false;
      if (p.estado === 'pendiente' || p.estado === 'parcial') {
        this.openPagarModal(p);
      } else {
        this.openEditModal(p);
      }
    },

    // ============================================
    // AUTOCOMPLETE DE CONTRATO
    // ============================================
    seleccionarContrato(c) {
      this.contratoSeleccionado = c;
      this.formPago.idContratoServicio = c.idContratoServicio;
      this.busquedaContrato = '';
      this.mostrarListaContratos = false;
    },
    limpiarContrato() {
      this.contratoSeleccionado = null;
      this.formPago.idContratoServicio = 0;
      this.busquedaContrato = '';
    },
    cerrarListaContratosDelayed() {
      setTimeout(() => { this.mostrarListaContratos = false; }, 150);
    },

    // ============================================
    // CREAR / EDITAR
    // ============================================
    openPagoModal() {
      this.editing = null;
      this.contratoSeleccionado = null;
      this.busquedaContrato = '';
      this.mostrarListaContratos = false;
      this.formPago = {
        idContratoServicio: 0,
        numero_recibo: '',
        periodo: '',
        fecha_emision: new Date().toISOString().slice(0, 10),
        fecha_vencimiento: '',
        monto_original: 0,
        monto_mora: 0,
        observaciones: ''
      };
      this.showPagoModal = true;
    },
    openEditModal(p) {
      this.editing = p;
      this.busquedaContrato = '';
      this.mostrarListaContratos = false;
      this.contratoSeleccionado = this.contratos.find(c => c.idContratoServicio === p.idContratoServicio) || null;
      this.formPago = {
        idContratoServicio: p.idContratoServicio,
        numero_recibo: p.numero_recibo || '',
        periodo: p.periodo || '',
        fecha_emision: p.fecha_emision ? p.fecha_emision.slice(0, 10) : '',
        fecha_vencimiento: p.fecha_vencimiento ? p.fecha_vencimiento.slice(0, 10) : '',
        monto_original: p.monto_original,
        monto_mora: p.monto_mora,
        observaciones: p.observaciones || ''
      };
      this.showPagoModal = true;
    },
    closePagoModal() {
      this.showPagoModal = false;
      this.editing = null;
      this.contratoSeleccionado = null;
      this.busquedaContrato = '';
      this.mostrarListaContratos = false;
    },
    async savePago() {
      if (!this.formPago.idContratoServicio) {
        this.showWarning('Debe seleccionar un contrato');
        return;
      }
      if (!this.formPago.fecha_vencimiento) {
        this.showWarning('La fecha de vencimiento es obligatoria');
        return;
      }
      if (!this.formPago.monto_original || this.formPago.monto_original <= 0) {
        this.showWarning('El monto debe ser mayor a 0');
        return;
      }
      this.saving = true;
      try {
        let res;
        if (this.editing) {
          res = await actualizarPago(this.editing.idPagoServicio, this.formPago);
        } else {
          res = await crearPago(this.formPago);
        }
        if (res.success) {
          await this.cargarTodo();
          this.closePagoModal();
          this.showSuccess(this.editing ? 'Factura actualizada' : 'Factura creada');
        } else {
          this.showError(res.error || 'No se pudo guardar');
        }
      } catch (e) {
        this.showError(e.message);
      } finally {
        this.saving = false;
      }
    },

    // ============================================
    // REGISTRAR PAGO
    // ============================================
    openPagarModal(p) {
      this.pagoTarget = p;
      this.tasaActualInfo = null;
      this.formPagar = {
        monto_pagado: p.saldo_pendiente || p.monto_total,
        metodo_pago: 'efectivo',
        referencia: '',
        tasa_bs_usd: p.tasa_bs_usd || null,
        monto_bs: p.monto_bs || null,
        observaciones: ''
      };
      this.recalcularMontoBs();
      this.showPagarModal = true;
    },
    closePagarModal() {
      this.showPagarModal = false;
      this.pagoTarget = null;
      this.tasaActualInfo = null;
    },

    async traerTasaActual() {
      this.cargandoTasa = true;
      try {
        const res = await obtenerTasaVentaActual();
        if (res.success && res.data) {
          const tasa = res.data;
          this.formPagar.tasa_bs_usd = parseFloat(tasa.precio_usd_bs || tasa.precioUsdBs || 0);
          this.tasaActualInfo = {
            fuente: tasa.nombreFuente || tasa.fuente || 'Sistema',
            fecha: tasa.fecha || new Date().toISOString()
          };
          this.recalcularMontoBs();
          this.showSuccess(`Tasa actualizada: ${this.formatearBs(this.formPagar.tasa_bs_usd)} Bs/USD`);
        } else {
          this.showWarning('No se pudo obtener la tasa actual');
        }
      } catch (e) {
        console.error(e);
        this.showError('Error al obtener la tasa');
      } finally {
        this.cargandoTasa = false;
      }
    },

    recalcularMontoBs() {
      const monto = Number(this.formPagar.monto_pagado) || 0;
      const tasa = Number(this.formPagar.tasa_bs_usd) || 0;
      if (monto > 0 && tasa > 0) {
        this.formPagar.monto_bs = parseFloat((monto * tasa).toFixed(2));
      } else {
        this.formPagar.monto_bs = 0;
      }
    },

    async ejecutarPago() {
      if (!this.pagoTarget) return;
      this.pagando = true;

      const pagoOriginal = { ...this.pagoTarget };
      const contrato = this.contratos.find(c => c.idContratoServicio === pagoOriginal.idContratoServicio);

      try {
        const res = await registrarPago(this.pagoTarget.idPagoServicio, this.formPagar);
        if (res.success) {
          await this.cargarTodo();
          this.closePagarModal();
          this.showSuccess('Pago registrado correctamente');

          if (contrato && contrato.periodicidad && contrato.periodicidad !== 'unico') {
            const proximaExistente = this.obtenerProximaFacturaDelContrato(
              pagoOriginal.idContratoServicio,
              pagoOriginal.periodo
            );

            if (!proximaExistente) {
              setTimeout(() => {
                this.abrirPostPagoModal(pagoOriginal, contrato);
              }, 500);
            }
          }
        } else {
          this.showError(res.error || 'No se pudo registrar el pago');
        }
      } catch (e) {
        this.showError(e.message);
      } finally {
        this.pagando = false;
      }
    },

    // ============================================
    // POST-PAGO
    // ============================================
    calcularPeriodoSiguiente(periodoActual, periodicidad) {
      if (!periodoActual || !/^\d{4}-\d{2}$/.test(periodoActual)) {
        const hoy = new Date();
        return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
      }

      const [anio, mes] = periodoActual.split('-').map(Number);
      let nuevaFecha = new Date(anio, mes - 1, 1);

      switch ((periodicidad || 'mensual').toLowerCase()) {
        case 'mensual': nuevaFecha.setMonth(nuevaFecha.getMonth() + 1); break;
        case 'bimestral': nuevaFecha.setMonth(nuevaFecha.getMonth() + 2); break;
        case 'trimestral': nuevaFecha.setMonth(nuevaFecha.getMonth() + 3); break;
        case 'semestral': nuevaFecha.setMonth(nuevaFecha.getMonth() + 6); break;
        case 'anual': nuevaFecha.setFullYear(nuevaFecha.getFullYear() + 1); break;
        default: nuevaFecha.setMonth(nuevaFecha.getMonth() + 1);
      }

      return `${nuevaFecha.getFullYear()}-${String(nuevaFecha.getMonth() + 1).padStart(2, '0')}`;
    },

    obtenerProximaFacturaDelContrato(idContratoServicio, periodoActual) {
      return this.pagos
        .filter(p =>
          p.idContratoServicio === idContratoServicio &&
          p.periodo !== periodoActual &&
          (p.estado === 'pendiente' || p.estado === 'parcial') &&
          new Date(p.fecha_vencimiento) > new Date()
        )
        .sort((a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento))[0];
    },

    abrirPostPagoModal(pagoOriginal, contrato) {
      this.postPagoTarget = {
        idContratoServicio: pagoOriginal.idContratoServicio,
        aliasContrato: pagoOriginal.aliasContrato,
        nombreServicio: pagoOriginal.nombreServicio,
        contratoAlias: contrato?.alias || contrato?.numero_contrato || '',
        periodicidad: contrato?.periodicidad || 'mensual',
        montoEstimado: pagoOriginal.monto_original || contrato?.monto_estimado || 0,
        diasParaVencimiento: contrato?.dia_vencimiento || 15
      };
      this.postPagoPeriodo = this.calcularPeriodoSiguiente(pagoOriginal.periodo, contrato?.periodicidad);
      this.postPagoFechaVencimiento = '';
      this.showPostPagoModal = true;
    },

    cerrarPostPagoModal() {
      this.showPostPagoModal = false;
      this.postPagoTarget = null;
      this.postPagoPeriodo = '';
      this.postPagoFechaVencimiento = '';
    },

    async crearSiguienteFactura() {
      if (!this.postPagoTarget) return;
      if (!this.postPagoFechaVencimiento) {
        this.showWarning('La fecha de vencimiento es obligatoria');
        return;
      }

      this.postPagoCreando = true;
      try {
        const hoy = new Date().toISOString().slice(0, 10);

        const payload = {
          idContratoServicio: this.postPagoTarget.idContratoServicio,
          numero_recibo: '',
          periodo: this.postPagoPeriodo,
          fecha_emision: hoy,
          fecha_vencimiento: this.postPagoFechaVencimiento,
          monto_original: this.postPagoTarget.montoEstimado,
          monto_mora: 0,
          observaciones: 'Factura generada automáticamente después del pago'
        };

        const res = await crearPago(payload);
        if (res.success) {
          const periodoCreado = this.postPagoPeriodo;
          await this.cargarTodo();
          this.cerrarPostPagoModal();
          this.showSuccess(`Factura ${this.formatearPeriodo(periodoCreado)} creada correctamente`);
        } else {
          this.showError(res.error || 'No se pudo crear la siguiente factura');
        }
      } catch (e) {
        this.showError(e.message);
      } finally {
        this.postPagoCreando = false;
      }
    },

    // ============================================
    // DESCARGAR FACTURA
    // ============================================
    descargarFactura(p) {
      const colorServ = this.getColorServicio(p);
      const html = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<title>Factura ${p.numero_recibo || p.idPagoServicio}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Arial', sans-serif; padding: 20px; background: #f5f5f5; }
  .factura { max-width: 700px; margin: 0 auto; background: white; padding: 30px; border-radius: 10px; }
  .header { display: flex; justify-content: space-between; align-items: start; border-bottom: 3px solid #2563eb; padding-bottom: 15px; margin-bottom: 20px; }
  .icono { width: 60px; height: 60px; border-radius: 15px; display: flex; align-items: center; justify-content: center; font-size: 30px; }
  .empresa { font-size: 20px; font-weight: bold; color: #1e293b; }
  .titulo { font-size: 24px; font-weight: 900; color: #2563eb; text-align: right; }
  .subtitulo { font-size: 11px; color: #64748b; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
  .info-item { padding: 12px; background: #f8fafc; border-radius: 8px; border-left: 3px solid #2563eb; }
  .info-label { font-size: 10px; font-weight: 900; color: #64748b; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
  .info-valor { font-size: 14px; font-weight: bold; color: #1e293b; }
  .tabla { width: 100%; border-collapse: collapse; margin-top: 20px; }
  .tabla th { background: #f1f5f9; padding: 12px; text-align: left; font-size: 11px; text-transform: uppercase; color: #475569; }
  .tabla td { padding: 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
  .total-row { display: flex; justify-content: space-between; padding: 15px 0; border-top: 3px solid #2563eb; margin-top: 15px; }
  .total-label { font-size: 16px; font-weight: 900; color: #1e293b; }
  .total-valor { font-size: 24px; font-weight: 900; color: #2563eb; }
  .estado { display: inline-block; padding: 6px 14px; border-radius: 20px; font-size: 11px; font-weight: 900; text-transform: uppercase; }
  .estado-pagado { background: #d1fae5; color: #065f46; }
  .estado-pendiente { background: #fef3c7; color: #92400e; }
  .estado-vencido { background: #fee2e2; color: #991b1b; }
  .estado-parcial { background: #fed7aa; color: #9a3412; }
  .estado-anulado { background: #e5e7eb; color: #4b5563; }
  .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 10px; color: #94a3b8; }
  .botones { text-align: center; margin-top: 20px; }
  .btn { padding: 10px 20px; margin: 0 5px; border: none; border-radius: 8px; font-weight: bold; cursor: pointer; font-size: 13px; }
  .btn-print { background: #2563eb; color: white; }
  .btn-close { background: #e5e7eb; color: #475569; }
  @media print { body { background: white; padding: 0; } .factura { box-shadow: none; padding: 0; } .botones { display: none; } }
</style>
</head>
<body>
<div class="factura">
  <div class="header">
    <div style="display: flex; align-items: center; gap: 15px;">
      <div class="icono" style="background: ${colorServ}20; color: ${colorServ};">
        <span style="font-size: 30px;">●</span>
      </div>
      <div>
        <div class="empresa">${p.nombreServicio || p.tipoServicio || 'Servicio Público'}</div>
        <div class="subtitulo">${p.proveedor || ''}</div>
      </div>
    </div>
    <div>
      <div class="titulo">FACTURA</div>
      <div class="subtitulo">${p.numero_recibo || 'Sin recibo'}</div>
    </div>
  </div>

  <div style="margin-bottom: 15px;">
    <span class="estado estado-${p.estado}">${p.estado}</span>
  </div>

  <div class="info-grid">
    <div class="info-item">
      <div class="info-label">Contrato / Alias</div>
      <div class="info-valor">${p.aliasContrato || 'Sin alias'}</div>
    </div>
    <div class="info-item">
      <div class="info-label">Período</div>
      <div class="info-valor">${this.formatearPeriodo(p.periodo) || 'Sin período'}</div>
    </div>
    <div class="info-item">
      <div class="info-label">Fecha Emisión</div>
      <div class="info-valor">${this.formatearFecha(p.fecha_emision) || '—'}</div>
    </div>
    <div class="info-item">
      <div class="info-label">Fecha Vencimiento</div>
      <div class="info-valor">${this.formatearFecha(p.fecha_vencimiento)}</div>
    </div>
    <div class="info-item">
      <div class="info-label">Método de Pago</div>
      <div class="info-valor">${p.metodo_pago || '—'}</div>
    </div>
    <div class="info-item">
      <div class="info-label">Fecha Pago</div>
      <div class="info-valor">${this.formatearFecha(p.fecha_pago) || '—'}</div>
    </div>
  </div>

  <table class="tabla">
    <thead>
      <tr>
        <th>Concepto</th>
        <th style="text-align: right;">Monto USD</th>
      </tr>
    </thead>
    <tbody>
      <tr><td>Monto Original</td><td style="text-align: right;">${this.formatearMoneda(p.monto_original)}</td></tr>
      <tr><td>Mora</td><td style="text-align: right;">${this.formatearMoneda(p.monto_mora)}</td></tr>
      <tr><td>Monto Pagado</td><td style="text-align: right; color: #059669; font-weight: bold;">${this.formatearMoneda(p.monto_pagado)}</td></tr>
      <tr><td>Saldo Pendiente</td><td style="text-align: right; color: #dc2626; font-weight: bold;">${this.formatearMoneda(p.saldo_pendiente)}</td></tr>
    </tbody>
  </table>

  <div class="total-row">
    <span class="total-label">TOTAL A PAGAR</span>
    <span class="total-valor">${this.formatearMoneda(p.monto_total)}</span>
  </div>

  ${p.monto_bs ? `
  <div style="margin-top: 15px; padding: 12px; background: #f0fdf4; border-radius: 8px; text-align: right;">
    <span style="font-size: 11px; color: #64748b; text-transform: uppercase;">Monto en Bolívares</span>
    <div style="font-size: 20px; font-weight: 900; color: #059669;">Bs. ${this.formatearBs(p.monto_bs)}</div>
    <span style="font-size: 10px; color: #64748b;">Tasa: ${this.formatearBs(p.tasa_bs_usd)} Bs/USD</span>
  </div>
  ` : ''}

  ${p.observaciones ? `
  <div style="margin-top: 15px; padding: 12px; background: #f8fafc; border-radius: 8px;">
    <div class="info-label">Observaciones</div>
    <div style="font-size: 12px; color: #475569; margin-top: 4px;">${p.observaciones}</div>
  </div>
  ` : ''}

  <div class="footer">
    Documento generado el ${new Date().toLocaleString('es-VE')}
  </div>
</div>

<div class="botones">
  <button class="btn btn-print" onclick="window.print()">🖨️ IMPRIMIR / GUARDAR PDF</button>
  <button class="btn btn-close" onclick="window.close()">✖️ CERRAR</button>
</div>
</body>
</html>`;

      const ventana = window.open('', '_blank', 'width=900,height=700,menubar=yes,toolbar=yes,scrollbars=yes');
      if (!ventana) {
        this.showWarning('Por favor, permite ventanas emergentes para descargar la factura');
        return;
      }
      ventana.document.write(html);
      ventana.document.close();
      ventana.focus();
      this.showSuccess('Factura generada — puedes imprimirla o guardarla como PDF');
    },

    // ============================================
    // ELIMINAR
    // ============================================
    abrirModalEliminar(p) {
      this.confirmTarget = p;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmTarget = null;
      this.confirmLoading = false;
    },
    async ejecutarEliminar() {
      if (!this.confirmTarget) return;
      this.confirmLoading = true;
      try {
        const res = await eliminarPago(this.confirmTarget.idPagoServicio);
        if (res.success) {
          await this.cargarTodo();
          this.showSuccess('Factura eliminada correctamente');
        } else {
          this.showError(res.error || 'No se pudo eliminar');
        }
      } catch (e) {
        this.showError(e.message);
      } finally {
        this.confirmLoading = false;
        this.cancelarConfirmacion();
      }
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
</style>